import { cn } from "@/lib/utils";
import React, { FocusEvent, useState } from "react";

interface SelectDropDownProps {
  label: string;
  required?: boolean;
  optionsList: { 
    id: number | string; 
    label: string; 
    icon?: any;
    subTitles?: { id: string; label: string }[];
  }[];
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  value: number | string;
  errorMessage?: string;
  placeholder?: string;
  onFocus?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
}

const SelectDropDown = (props: SelectDropDownProps) => {
  const {
    label,
    required,
    optionsList,
    onChange,
    value,
    errorMessage,
    placeholder,
    onFocus,
    onBlur,
  } = props;

  const [active, setActive] = useState(false);

  const handleFocus = (event: React.FocusEvent<HTMLSelectElement>) => {
    setActive(true);
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLSelectElement>) => {
    setActive(false);
    if (onBlur) {
      onBlur(event);
    }
  };

  return (
    <div>
      <div
        className={cn(
          "flex justify-center items-center relative h-[52px] rounded-[4px] ",
          errorMessage
            ? "border-2 border-[#F87171] dark:border-[#FCA5A5]"
            : active
              ? "border-2 border-primary-500 dark:border-primary-300 bg-primary-50 dark:bg-primary-900"
              : "border-2 border-neutral-200 dark:border-neutral-200 bg-primary-50 dark:bg-primary-900"
        )}
      >
        <select
          className={[
            "appearance-none outline-none w-full rounded text-sm transition-all duration-200 ease-in-out px-2 bg-transparent relative",
            active ? "pt-3" : "pt-3",
          ].join(" ")}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-label={placeholder || label}
        >
          {optionsList?.map((item) => {
            // If the item has subTitles, render them in an optgroup
            if (item.subTitles && item.subTitles.length > 0) {
              return (
                <optgroup key={item.id} label={item.label}>
                  {item.subTitles.map((subItem) => (
                    <option key={`${item.id}.${subItem.id}`} value={`${item.id}.${subItem.id}`}>
                      {subItem.label}
                    </option>
                  ))}
                </optgroup>
              );
            }
            // If no subTitles, render as a regular option
            return (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            );
          })}
        </select>

        {/* Custom arrow */}
        <div className="absolute right-2 pointer-events-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-neutral-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        <label
          className={[
            "absolute left-0 flex items-center px-2 transition-all duration-200 ease-in-out text-xs top-[6px]",
            errorMessage
              ? "text-[#F87171] dark:text-[#FCA5A5]"
              : active
                ? "!text-primary-500 dark:!text-primary-300"
                : "text-neutral-600 dark:text-neutral-600",
          ].join(" ")}
          htmlFor={label}
        >
          {placeholder || label}{" "}
          {required && <span className="text-[#F87171]">*</span>}
        </label>
      </div>
    </div>
  );
};

export default SelectDropDown;
